import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Menu, User, Star, Trophy, Mic2, FileText, Settings, LogOut, Edit3, Calendar, Clock, Check, X, TrendingUp, Download, CheckCircle, Activity, Music, Shirt, Map, Users, Gift } from 'lucide-react';
import { formatErrorMessage } from '../utils/errorFormatter';
import '../styles/CandidateArea.css';

const CandidateArea = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [sessionResults, setSessionResults] = useState([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [resultsError, setResultsError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [activeMenu, setActiveMenu] = useState('perfil');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [editError, setEditError] = useState('');
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    artistic_name: '',
    birth_date: '',
    address: '',
    phone: '',
    song_name: ''
  });
  const [isIncompleteProfileModalOpen, setIsIncompleteProfileModalOpen] = useState(false);
  const [incompleteProfileItems, setIncompleteProfileItems] = useState([]);
  const [sessionPublicVotesMap, setSessionPublicVotesMap] = useState({}); // sessionId -> valid public votes for current user
  const API_FESTIVAL_BASE_URL = process.env.API_FESTIVAL;
  const DEFAULT_USER_IMAGE = '/img/user.jpg';

  const PHASE_LABELS = {
    inscrito: 'Inscrito',
    audicao: 'Audição',
    final: 'Final',
    vencedor: 'Vencedor'
  };

  const SCORE_CRITERIA = [
    { key: 'afinacao', label: 'Afinação' },
    { key: 'presenca_palco', label: 'Presença de palco' },
    { key: 'melodia_harmonia', label: 'Melodia e harmonia' },
    { key: 'ritmo', label: 'Ritmo' },
    { key: 'interpretacao', label: 'Interpretação' },
    { key: 'autenticidade', label: 'Autenticidade / música autoral' },
    { key: 'diccao', label: 'Dicção / pronúncia' }
  ];

  const readCriterionFromResult = (result, key) => {
    if (!result) return null;

    // common shapes: result.criteria = { key: value }
    if (result.criteria && typeof result.criteria === 'object' && key in result.criteria) return Number(result.criteria[key]);
    // alternative shapes
    if (result.scores && typeof result.scores === 'object' && key in result.scores) return Number(result.scores[key]);
    if (result.breakdown && typeof result.breakdown === 'object' && key in result.breakdown) return Number(result.breakdown[key]);
    if (key in result && (typeof result[key] === 'number' || typeof result[key] === 'string')) return Number(result[key]);

    // try snake/other variants
    const altKeys = [key, key.replace(/_/g, ''), key.replace(/_/g, '-'), key.replace(/_/g, ' '), `${key}_score`];
    for (const k of altKeys) {
      if (result[k] !== undefined) return Number(result[k]);
    }

    return null;
  };

  const extractJudgeVotes = (result) => {
    if (!result) return [];

    // 1) result.judges: array of judge objects with scores
    if (Array.isArray(result.judges) && result.judges.length) {
      return result.judges.map((j) => ({
        id: j.id ?? j.judge_id ?? j.user_id ?? null,
        name: j.name || j.full_name || j.display_name || j.label || `Jurado ${j.id || ''}`,
        avatar: j.profile_photo_url || j.avatar || j.avatarUrl || '',
        scores: j.scores || j.criteria || j.breakdown || {}
      }));
    }

    // 2) result.votes: array of vote objects { judge, scores }
    if (Array.isArray(result.votes) && result.votes.length) {
      return result.votes.map((v) => ({
        id: v.judge?.id ?? v.judge_id ?? v.user_id ?? null,
        name: v.judge?.name || v.judge_name || v.judge?.full_name || v.name || `Jurado ${v.judge_id || ''}`,
        avatar: v.judge?.profile_photo_url || v.judge?.avatar || '',
        scores: v.scores || v.criteria || v.breakdown || {}
      }));
    }

    // 3) result.judge_scores as object mapping judgeId -> scores
    if (result.judge_scores && typeof result.judge_scores === 'object') {
      return Object.keys(result.judge_scores).map((judgeKey) => {
        const entry = result.judge_scores[judgeKey] || {};
        return {
          id: entry.judge_id ?? entry.id ?? judgeKey,
          name: entry.judge_name || entry.name || `Jurado ${judgeKey}`,
          avatar: entry.profile_photo_url || entry.avatar || '',
          scores: entry.scores || entry.criteria || entry.breakdown || (typeof entry === 'object' ? entry : {})
        };
      });
    }

    // 4) result.judges_scores or other variants
    const altKeys = ['judges_scores', 'judgeScores', 'judge_votes', 'judges_votes'];
    for (const k of altKeys) {
      if (result[k] && typeof result[k] === 'object') {
        if (Array.isArray(result[k])) {
          return result[k].map((entry) => ({
            id: entry.judge_id ?? entry.id ?? null,
            name: entry.judge_name || entry.name || `Jurado ${entry.judge_id || ''}`,
            avatar: entry.profile_photo_url || entry.avatar || '',
            scores: entry.scores || entry.criteria || entry.breakdown || {}
          }));
        }

        return Object.keys(result[k]).map((judgeKey) => {
          const entry = result[k][judgeKey] || {};
          return {
            id: entry.judge_id ?? entry.id ?? judgeKey,
            name: entry.judge_name || entry.name || `Jurado ${judgeKey}`,
            avatar: entry.profile_photo_url || entry.avatar || '',
            scores: entry.scores || entry.criteria || entry.breakdown || (typeof entry === 'object' ? entry : {})
          };
        });
      }
    }

    // 5) fallback: if result.per_judge or result.perJudge
    if (Array.isArray(result.per_judge)) {
      return result.per_judge.map((entry) => ({
        id: entry.judge_id ?? entry.id ?? null,
        name: entry.judge_name || entry.name || `Jurado ${entry.judge_id || ''}`,
        avatar: entry.profile_photo_url || entry.avatar || '',
        scores: entry.scores || entry.criteria || entry.breakdown || {}
      }));
    }

    return [];
  };

  const STATUS_LABELS = {
    ativo: 'ATIVO',
    eliminado: 'ELIMINADO',
    desistente: 'DESISTENTE'
  };

  // Função para verificar itens faltando no perfil
  const checkIncompleteProfileItems = (prof, prof_photo) => {
    const missing = [];
    
    if (!prof_photo || prof_photo === DEFAULT_USER_IMAGE) {
      missing.push('Foto de perfil');
    }
    
    if (!prof?.birth_date) {
      missing.push('Data de nascimento');
    }
    
    if (!prof?.song_name) {
      missing.push('Música para apresentação');
    }
    
    return missing;
  };

  const logoutCandidate = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login-candidato', { replace: true });
  }, [navigate]);

  const isAuthSessionExpiredError = useCallback((error) => {
    const status = error?.response?.status;
    const requestUrl = String(error?.config?.url || '').toLowerCase();
    const backendMessage = String(error?.response?.data?.message || error?.message || '').toLowerCase();
    const isCandidateMeRoute = requestUrl.includes('/api/candidate/me');
    const hasTokenHint = /token|jwt|expir|unauthor|inv[aá]lid|sess[aã]o/.test(backendMessage);

    if (status === 401 || status === 403) return true;

    // Some backend paths may return 404 for invalid/expired auth on this endpoint.
    if (status === 404 && isCandidateMeRoute && hasTokenHint) {
      return true;
    }

    return false;
  }, []);

  const formatSessionDate = (dateValue) => {
    if (!dateValue) return 'Data não definida';
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return 'Data não definida';
    return parsed.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatSessionTime = (timeValue) => {
    if (!timeValue) return 'Horário não definido';
    if (typeof timeValue === 'string') return timeValue.slice(0, 5);
    return 'Horário não definido';
  };

  const getCandidateCode = (id) => {
    if (!id) return '#FORRO-----';
    return `#FORRO-${String(id).padStart(4, '0')}`;
  };

  const resolveProfilePhotoUrl = useCallback((photoUrl) => {
    if (!photoUrl) return DEFAULT_USER_IMAGE;

    if (
      photoUrl.startsWith('http://') ||
      photoUrl.startsWith('https://') ||
      photoUrl.startsWith('data:') ||
      photoUrl.startsWith('blob:') ||
      photoUrl.startsWith('/img/')
    ) {
      return photoUrl;
    }

    const normalizedPath = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;
    return `${API_FESTIVAL_BASE_URL}${normalizedPath}`;
  }, [API_FESTIVAL_BASE_URL, DEFAULT_USER_IMAGE]);

  useEffect(() => {
    const bootstrapCandidateArea = async () => {
      setIsLoadingProfile(true);
      setIsLoadingResults(false);
      setProfileError('');
      setResultsError('');
      setProfileSuccess('');

      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      let parsedUser = null;

      if (userData) {
        try {
          parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (error) {
          localStorage.removeItem('user');
        }
      }

      if (!token) {
        navigate('/login-candidato');
        return;
      }

      try {
        const [profileResponse, sessionsResponse] = await Promise.all([
          axios.get(`${API_FESTIVAL_BASE_URL}/api/candidate/me`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_FESTIVAL_BASE_URL}/api/candidate/sessions`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (profileResponse?.data?.user) {
          setUser(profileResponse.data.user);
          localStorage.setItem('user', JSON.stringify(profileResponse.data.user));
        } else if (parsedUser) {
          setUser(parsedUser);
        }

        const normalizedSessions = Array.isArray(sessionsResponse?.data?.sessions) ? sessionsResponse.data.sessions : [];

        setProfile(profileResponse?.data?.profile || null);
        setSessions(normalizedSessions);

        // Verificar itens faltando no perfil
        const profileData = profileResponse?.data?.profile || null;
        const profilePhoto = resolveProfilePhotoUrl(profileResponse?.data?.user?.profile_photo_url || profileResponse?.data?.profile?.profile_photo_url || DEFAULT_USER_IMAGE);
        const missingItems = checkIncompleteProfileItems(profileData, profilePhoto);
        
        if (missingItems.length > 0 && profileData?.status !== 'eliminado') {
          setIncompleteProfileItems(missingItems);
          setIsIncompleteProfileModalOpen(true);
        }

        const finishedSessions = normalizedSessions.filter((session) => session.session_status === 'finished');
        
        // NOVO: Define a sessão finalizada mais recente como a selecionada por padrão
        if (finishedSessions.length > 0) {
          const sortedFinished = [...finishedSessions].sort((a, b) => Number(b.id) - Number(a.id));
          setSelectedSessionId(String(sortedFinished[0].id));
        }

        if (!finishedSessions.length) {
          setSessionResults([]);
        } else {
          setIsLoadingResults(true);

          try {
            const resultResponses = await Promise.all(
              finishedSessions.map(async (session) => {
                try {
                  const response = await axios.get(`${API_FESTIVAL_BASE_URL}/api/candidate/results/${session.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });

                  return response?.data || null;
                } catch (error) {
                  if (isAuthSessionExpiredError(error)) {
                    throw error;
                  }
                  return null;
                }
              })
            );

            setSessionResults(resultResponses.filter(Boolean));
          } catch (error) {
            setResultsError('Não foi possível carregar os resultados das sessões finalizadas.');
            setSessionResults([]);
          } finally {
            setIsLoadingResults(false);
          }
        }
      } catch (error) {
        if (isAuthSessionExpiredError(error)) {
          logoutCandidate();
          return;
        }

        setProfileError(error?.response?.data?.message || 'Não foi possível carregar os dados do candidato.');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    bootstrapCandidateArea();
  }, [API_FESTIVAL_BASE_URL, isAuthSessionExpiredError, logoutCandidate, navigate, resolveProfilePhotoUrl]);

  

  // --- INÍCIO DA LÓGICA DE DADOS DINÂMICOS ---
  const finishedSessionsList = sessions.filter(s => s.session_status === 'finished');

  const currentActiveResult = selectedSessionId 
    ? sessionResults.find(r => Number(r?.session?.id) === Number(selectedSessionId)) || null
    : sessionResults[0] || null;

  const currentActiveSessionSummary = selectedSessionId
    ? sessions.find(s => Number(s.id) === Number(selectedSessionId)) || null
    : sessions.find(s => s.session_status === 'finished') || null;

  const nextSession = sessions.find((session) => session.session_status !== 'finished') || sessions[0] || null;
  const finishedSessionsCount = finishedSessionsList.length;

  const upcomingSessionsCount = sessions.filter((session) => session.session_status !== 'finished').length;

  const hasScheduleInfo = Boolean(nextSession && (nextSession.session_date || nextSession.session_time || nextSession.location));

  // Verificação de exibição baseada no resultado selecionado
  const hasRealNotes = currentActiveResult !== null;
  const hasNotesInfo = hasRealNotes; 

  // Fetch session-level valid public votes (from transmission results) and cache them
  useEffect(() => {
    const fetchSessionValidVotes = async (sessionIdToFetch) => {
      if (!sessionIdToFetch) return;
      if (sessionPublicVotesMap?.[sessionIdToFetch] !== undefined) return; // already fetched

      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const res = await axios.get(`${API_FESTIVAL_BASE_URL}/api/candidate/results/${sessionIdToFetch}`, { headers });
        const rows = res?.data?.popular_ranking || res?.data?.popular || [];
        if (Array.isArray(rows) && rows.length) {
          const entry = rows.find((r) => Number(r.candidate_id ?? r.id) === Number(user?.id));
          const val = entry ? Number(entry.public_votes ?? entry.votes ?? entry.count ?? 0) : null;
          setSessionPublicVotesMap((prev) => ({ ...(prev || {}), [sessionIdToFetch]: val }));
        } else {
          setSessionPublicVotesMap((prev) => ({ ...(prev || {}), [sessionIdToFetch]: null }));
        }
      } catch (err) {
        setSessionPublicVotesMap((prev) => ({ ...(prev || {}), [sessionIdToFetch]: null }));
      }
    };

    if (selectedSessionId) {
      fetchSessionValidVotes(selectedSessionId);
    } else if (currentActiveResult?.session?.id) {
      fetchSessionValidVotes(currentActiveResult.session.id);
    }
  }, [selectedSessionId, currentActiveResult, API_FESTIVAL_BASE_URL, user?.id, sessionPublicVotesMap]);

  const candidatePopularData = currentActiveResult?.popular_ranking?.find(r => Number(r.candidate_id) === Number(user?.id));
  const candidateTechnicalData = currentActiveResult?.technical_ranking?.find(r => Number(r.candidate_id) === Number(user?.id));

  // 1. MÉDIA DOS JURADOS: Força a leitura do 9.44 real
  const latestTechnicalScore = hasRealNotes ? Number(candidateTechnicalData?.judge_average || currentActiveResult?.total_score || 0) : Number(currentActiveSessionSummary?.total_score || 0);

  // 2. VOTOS PÚBLICOS: extrai votos públicos de várias estruturas possíveis
  const readPublicVotesFromResult = (result) => {
    if (!result) return 0;

    // 1) chave direta comum
    const direct = result.public_votes ?? result.audience_votes ?? result.public_votes_total ?? result.total_public_votes;
    if (direct !== undefined && direct !== null) return Number(direct || 0);

    // 2) popular_ranking: array com entradas por candidato
    const ranking = result.popular_ranking || result.popular || result.popular_votes;
    if (Array.isArray(ranking) && ranking.length && user?.id != null) {
      const entry = ranking.find((r) => Number(r.candidate_id ?? r.id) === Number(user.id));
      if (entry) return Number(entry.votes ?? entry.count ?? entry.public_votes ?? entry.total_votes ?? 0);
    }

    // 3) formas alternativas
    const alt = result.votes ?? result.vote_count ?? result.total_votes ?? 0;
    return Number(alt || 0);
  };

  let latestAdjustmentScore = Number(currentActiveSessionSummary?.manual_adjustment || 0);
  if (hasRealNotes) {
    const sessId = selectedSessionId || currentActiveResult?.session?.id;
    const cached = sessId ? (sessionPublicVotesMap?.[sessId] ?? sessionPublicVotesMap?.[String(sessId)]) : undefined;
    if (cached !== undefined && cached !== null) {
      latestAdjustmentScore = Number(cached || 0);
    } else {
      latestAdjustmentScore = readPublicVotesFromResult(currentActiveResult);
    }
  }

  // 3. PONTUAÇÃO FINAL: Força a leitura do 9.44 real para pintar a barra
  const latestEffectiveScore = hasRealNotes ? Number(candidateTechnicalData?.judge_average || currentActiveResult?.effective_score || 0) : 0;

  // Normaliza largura da barra de votos públicos (percentual relativo ao maior do ranking)
  const publicRanking = currentActiveResult?.popular_ranking || currentActiveResult?.popular || [];
  const publicMax = (Array.isArray(publicRanking) && publicRanking.length)
    ? Math.max(...publicRanking.map(r => Number(r.votes ?? r.count ?? r.public_votes ?? r.total_votes ?? 0)), latestAdjustmentScore || 1)
    : Math.max(1, latestAdjustmentScore || 1);
  const publicWidthPercent = Math.round((latestAdjustmentScore / publicMax) * 100);
  const publicBarWidth = `${Math.min(100, Math.max(0, publicWidthPercent))}%`;

  const latestScoredSession = currentActiveSessionSummary;

  const averageEffectiveScore = hasRealNotes
    ? sessionResults.reduce((acc, result) => acc + Number(result?.effective_score || 0), 0) / sessionResults.length
    : 0;

  // NOVA LÓGICA: Busca a posição real no ranking técnico
  const getCandidateTechPosition = (result) => {
    if (!result || !result.technical_ranking) return Number(result?.position || 0);
    const idx = result.technical_ranking.findIndex(r => Number(r.candidate_id) === Number(user?.id));
    return idx >= 0 ? idx + 1 : Number(result.position || 0);
  };

  const latestPosition = getCandidateTechPosition(currentActiveResult);
  const latestTotalCandidates = currentActiveResult?.technical_ranking?.length || Number(currentActiveResult?.total_candidates || 0);
  const latestWinnerMessage = currentActiveResult?.winner_message || '';
  
  const sortedResults = [...sessionResults].sort((a, b) => Number(b?.session?.id || 0) - Number(a?.session?.id || 0));
  const latestResult = sortedResults[0] || null;
  const bestResult = hasRealNotes ? [...sessionResults].sort((a, b) => Number(b?.effective_score || 0) - Number(a?.effective_score || 0))[0] : null;

  const competitionStateLabel = profile?.status === 'eliminado'
    ? 'ELIMINADO'
    : profile?.status === 'desistente'
      ? 'DESISTENTE'
      : currentActiveResult?.is_winner ? 'CLASSIFICADO' : 'ATIVO';

  const statusLabel = STATUS_LABELS[profile?.status] || 'ATIVO';
  const phaseLabel = PHASE_LABELS[profile?.current_phase] || 'Audição';
  const profilePhoto = resolveProfilePhotoUrl(profile?.profile_photo_url || user?.profile_photo_url || DEFAULT_USER_IMAGE);
  const modalPhotoPreview = photoPreviewUrl || profilePhoto;
  const canEditProfile = Boolean(profile?.can_edit ?? true);
  const canEditSongName = !profile?.song_name;
  // --- FIM DA LÓGICA DE DADOS DINÂMICOS ---

  const openEditModal = () => {
    if (profile?.status === 'eliminado') {
      return;
    }

    if (!user && !profile) {
      return;
    }

    setEditError('');
    setSelectedPhotoFile(null);
    setPhotoPreviewUrl('');
    setEditForm({
      name: user?.name || '',
      artistic_name: profile?.artistic_name || '',
      birth_date: profile?.birth_date ? String(profile.birth_date).slice(0, 10) : '',
      address: profile?.address || '',
      phone: profile?.phone || '',
      song_name: profile?.song_name || ''
    });
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (isSavingProfile) return;
    setIsEditModalOpen(false);
    setEditError('');
    setSelectedPhotoFile(null);
    if (photoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhotoPreviewUrl('');
  };

  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const formatPhoneNumber = (value) => {
    const cleaned = String(value || '').replace(/\D/g, '');
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
    if (cleaned.length <= 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  };

  const handlePhoneChange = (event) => {
    const rawValue = event.target.value;
    const formattedValue = formatPhoneNumber(rawValue);
    setEditForm((current) => ({ ...current, phone: formattedValue }));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setEditError('Envie uma imagem JPG, PNG ou WEBP.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setEditError('A imagem deve ter no máximo 10MB.');
      return;
    }

    if (photoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreviewUrl);
    }

    setEditError('');
    setSelectedPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();

    if (profile?.status === 'eliminado') {
      setEditError('Seu perfil está bloqueado. Candidatos eliminados não podem fazer alterações.');
      return;
    }

    if (!canEditProfile) {
      setEditError('Seu perfil está bloqueado para edição.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login-candidato');
      return;
    }

    const payload = {
      name: editForm.name.trim(),
      artistic_name: editForm.artistic_name.trim(),
      birth_date: editForm.birth_date || '',
      address: editForm.address.trim(),
      phone: editForm.phone.trim()
    };

    if (canEditSongName) {
      payload.song_name = editForm.song_name.trim();
    }

    if (!payload.name || !payload.artistic_name) {
      setEditError('Nome e nome artístico são obrigatórios.');
      return;
    }

    if (canEditSongName && !payload.song_name) {
      setEditError('Informe a música para concluir a atualização.');
      return;
    }

    setIsSavingProfile(true);
    setEditError('');
    setProfileSuccess('');

    try {
      if (selectedPhotoFile) {
        const uploadData = new FormData();
        uploadData.append('file', selectedPhotoFile);

        const uploadResponse = await axios.post(`${API_FESTIVAL_BASE_URL}/api/uploads/imagens`, uploadData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const uploadedReference = uploadResponse?.data?.fileReference;
        if (!uploadedReference) {
          throw new Error('Falha ao obter referência da imagem enviada.');
        }

        payload.profile_photo_url = uploadedReference;
      }

      const response = await axios.patch(`${API_FESTIVAL_BASE_URL}/api/candidate/me`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response?.data?.user) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      if (response?.data?.profile) {
        setProfile(response.data.profile);
      }

      setSelectedPhotoFile(null);
      if (photoPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(photoPreviewUrl);
      }
      setPhotoPreviewUrl('');

      setProfileSuccess(response?.data?.message || 'Perfil atualizado com sucesso.');
      setIsEditModalOpen(false);
    } catch (error) {
      if (isAuthSessionExpiredError(error)) {
        logoutCandidate();
        return;
      }

      const friendlyErrorMessage = formatErrorMessage(error);
      setEditError(friendlyErrorMessage);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleLogout = () => {
    logoutCandidate();
  };

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
    setIsMobileMenuOpen(false);
  };

  const openMobileMenu = () => {
    setIsMobileMenuOpen(true);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="candidate-area-page">
      <div className="candidate-layout">
        {isMobileMenuOpen && <button type="button" className="candidate-sidebar-backdrop" aria-label="Fechar menu" onClick={closeMobileMenu}></button>}
        
        {/* SIDEBAR */}
        <aside className={`candidate-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-logo">
            <h1>FESTIVAL DE<br/>FORRÓ</h1>
            <p>EDIÇÃO DIGITAL ARRAIAL</p>
          </div>
          
          <div className="sidebar-divider"></div>
          
          <div className="sidebar-user-mini">
            <img 
              src={profilePhoto}
              alt="Avatar" 
              className="mini-avatar"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = DEFAULT_USER_IMAGE;
              }}
            />
            <div className="mini-info">
              <h4>Área do Candidato</h4>
              <span>ID: {getCandidateCode(user?.id)}</span>
            </div>
          </div>

          <nav className="sidebar-nav">
            <button className={`nav-item ${activeMenu === 'perfil' ? 'active' : ''}`} onClick={() => handleMenuClick('perfil')}>
              <User size={18} /> Meu Perfil
            </button>
            <button className={`nav-item ${activeMenu === 'notas' ? 'active' : ''}`} onClick={() => handleMenuClick('notas')}>
              <Star size={18} /> Minhas Notas
            </button>
            <button className={`nav-item ${activeMenu === 'status' ? 'active' : ''}`} onClick={() => handleMenuClick('status')}>
              <Trophy size={18} /> Status Competição
            </button>
            <button className={`nav-item ${activeMenu === 'apresentacao' ? 'active' : ''}`} onClick={() => handleMenuClick('apresentacao')}>
              <Mic2 size={18} /> Apresentação
            </button>
          </nav>

          <div className="sidebar-bottom">
            {/* <button className="btn-yellow" disabled={profile?.status === 'eliminado'} title={profile?.status === 'eliminado' ? 'Bloqueado para candidatos eliminados' : ''}>VER INSCRIÇÃO</button> */}
            <button className="bottom-link" disabled={profile?.status === 'eliminado'} title={profile?.status === 'eliminado' ? 'Bloqueado para candidatos eliminados' : ''}>
              {/* <Settings size={15} /> CONFIGURAÇÕES */}
            </button>
            <button className="bottom-link red" onClick={handleLogout}>
              <LogOut size={15} /> SAIR
            </button>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="candidate-main">
          
          {/* HEADER */}
          <header className="main-header">
            <div className="header-top">
              <div className="header-title-area">
                <h2>ÁREA DO CANDIDATO</h2>
                <p>Gerencie sua jornada no coração do Nordeste.</p>
              </div>
              <div className="header-actions">
                <button type="button" className="mobile-menu-btn" onClick={openMobileMenu} aria-label="Abrir menu de navegação">
                  <Menu size={22} />
                </button>
                <div className="status-badge-container">
                  <div className={`status-badge ${profile?.status === 'eliminado' ? 'red' : ''}`}>{statusLabel}</div>
                  <div className="fase-text">Fase: {phaseLabel}</div>
                </div>
                <button className="bell-btn">
                  <Bell size={20} />
                </button>
              </div>
            </div>
          </header>
          
          <div className="main-divider"></div>

          {profile?.status === 'eliminado' && (
            <div style={{ backgroundColor: '#fee2e2', borderLeft: '4px solid #b91c1c', padding: '16px', marginBottom: '16px', color: '#991b1b', fontWeight: 600 }}>
              ⚠️ Seu perfil foi eliminado da competição. Você pode visualizar suas informações, mas não é permitido fazer alterações.
            </div>
          )}

                    {isLoadingProfile && (
                      <div style={{ marginBottom: '16px', color: '#0f172a', fontWeight: 600 }}>
                        Carregando informações do candidato...
                      </div>
                    )}

                    {profileError && (
                      <div style={{ marginBottom: '16px', color: '#b91c1c', fontWeight: 600 }}>
                        {profileError}
                      </div>
                    )}

                    {profileSuccess && (
                      <div style={{ marginBottom: '16px', color: '#166534', fontWeight: 600 }}>
                        {profileSuccess}
                      </div>
                    )}

                    {isLoadingResults && (
                      <div style={{ marginBottom: '16px', color: '#0f172a', fontWeight: 600 }}>
                        Carregando resultados das sessões finalizadas...
                      </div>
                    )}

                    {resultsError && (
                      <div style={{ marginBottom: '16px', color: '#b91c1c', fontWeight: 600 }}>
                        {resultsError}
                      </div>
                    )}

          {activeMenu === 'perfil' && (
          <div className="content-grid">
            
            {/* LEFT COLUMN */}
            <div className="left-column">
              
              {/* Meu Perfil Card */}
              <div className="card perfil-card">
                <div className="card-decor-tr"></div>
                <div className="perfil-header">
                  <User size={20} color="#1a6b32" />
                  <h3>Meu Perfil</h3>
                </div>
                
                <div className="perfil-avatar-container">
                  <div className="perfil-avatar-wrapper">
                    <img
                      src={profilePhoto}
                      alt="Perfil"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = DEFAULT_USER_IMAGE;
                      }}
                    />
                    <button className="edit-btn" onClick={openEditModal} type="button" disabled={!canEditProfile || profile?.status === 'eliminado'} title={profile?.status === 'eliminado' ? 'Perfil bloqueado para candidatos eliminados' : ''}>
                      <Edit3 size={24} strokeWidth={2.6} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>NOME ARTÍSTICO</label>
                  <input type="text" className="form-input" disabled value={profile?.artistic_name || 'Não informado'} />
                </div>
                <div className="form-group">
                  <label>MÚSICA QUE IRÁ CANTAR</label>
                  <input type="text" className="form-input" disabled value={profile?.song_name || 'Não informada'} />
                </div>

                <button className="btn-green-full" type="button" onClick={openEditModal} disabled={!canEditProfile || profile?.status === 'eliminado'}>
                  {profile?.status === 'eliminado' ? 'Perfil bloqueado (eliminado)' : canEditProfile ? 'Editar Perfil' : 'Perfil bloqueado'}
                </button>
              </div>

              {/* Sua Apresentacao Card */}
              <div className="card apresentacao-card">
                <div style={{ position: 'absolute', top: 15, right: 15, opacity: 0.2 }}>
                  <Calendar size={40} />
                </div>
                <h4>SUA APRESENTAÇÃO</h4>
                {hasScheduleInfo ? (
                  <>
                    <div className="apresentacao-icon-row">
                      <Calendar size={18} />
                      <span>{formatSessionDate(nextSession?.session_date)}</span>
                    </div>
                    <div className="apresentacao-icon-row">
                      <Clock size={18} />
                      <span>{formatSessionTime(nextSession?.session_time)}</span>
                    </div>
                    <div className="palco-info">
                      <span>PALCO PRINCIPAL</span>
                      <strong>{nextSession?.location || 'Local não definido'}</strong>
                    </div>
                  </>
                ) : (
                  <div style={{ marginTop: '14px', fontWeight: 600, lineHeight: 1.5 }}>
                    Você ainda não possui informações de data.
                    <br />
                    Aguarde ser agendado.
                  </div>
                )}
              </div>

            </div>

            {/* RIGHT COLUMN */}
            <div className="right-column">
              
              {/* Desempenho Geral Card */}
              <div className="card desempenho-card">
                {hasNotesInfo ? (
                  <>
                    <div className="voto-popular-badge">
                      <span>PONTUAÇÃO FINAL</span>
                      <strong>{latestEffectiveScore.toFixed(2)}</strong>
                    </div>

                    <div className="desempenho-header">
                      <h3>DESEMPENHO GERAL</h3>
                      <p>Resultado consolidado da última avaliação disponível.</p>
                    </div>

                    <div className="score-list">
                      <div className="score-item">
                        <div className="score-info">
                          <h4>Média dos Jurados</h4>
                          <p>Pontuação consolidada dos jurados na sessão.</p>
                        </div>
                        <div className="score-value">
                          <strong>{latestTechnicalScore.toFixed(2)}</strong>
                          <span>/ 10</span>
                        </div>
                      </div>

                      <div className="score-item">
                        <div className="score-info">
                          <h4>Votos Públicos</h4>
                          <p>Pontuação agregada dos votos do público.</p>
                        </div>
                        <div className="score-value">
                          <strong>{hasRealNotes ? latestAdjustmentScore.toLocaleString('pt-BR') : '--'}</strong>
                          <span>votos</span>
                        </div>
                      </div>
                    </div>

                    <div className="desempenho-divider"></div>

                    <div className="media-final-area">
                      <span>RESULTADO EFETIVO</span>
                      <div className="media-bar-container">
                        <div className="media-bar-fill" style={{ width: `${Math.min(100, Math.max(0, latestEffectiveScore * 10))}%` }}></div>
                      </div>
                      <div className="media-final-score">{latestEffectiveScore.toFixed(2)}</div>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '28px 0', lineHeight: 1.6, fontWeight: 600 }}>
                    Você ainda não possui notas.
                    <br />
                    Aguarde ser avaliado para ver as notas.
                  </div>
                )}
              </div>

              {/* Status Cards */}
              <div className="status-cards-row">
                <div className={`status-card active-status ${profile?.status === 'eliminado' ? 'red' : ''}`}>
                  <div className="status-icon-circle">
                    {profile?.status === 'eliminado' ? <X size={24} strokeWidth={3} /> : <Check size={24} strokeWidth={3} />}
                  </div>
                  <div className="status-info">
                    <h5>SITUAÇÃO ATUAL</h5>
                    <h4>{profile?.status === 'eliminado' ? 'PERFIL BLOQUEADO' : 'VOCÊ ESTÁ ATIVO'}</h4>
                    <p>{profile?.status === 'eliminado' ? 'Edição desativada para candidato eliminado.' : 'Continue ensaiando para a próxima fase!'}</p>
                  </div>
                </div>

                <div className={`status-card eliminated-status ${profile?.status === 'eliminado' ? 'red' : ''}`}>
                  <div className="status-icon-circle">
                    <X size={24} strokeWidth={3} />
                  </div>
                  <div className="status-info">
                    <h5>STATUS DE ELIMINAÇÃO</h5>
                    <h4>{profile?.status === 'eliminado' ? 'ELIMINADO' : 'NÃO ELIMINADO'}</h4>
                    <p>{profile?.status === 'eliminado' ? 'Campos bloqueados para edição.' : 'Seu cadastro segue ativo no festival.'}</p>
                  </div>
                </div>
              </div>

              {/* Duvidas Card */}
              <div className="duvidas-card">
                <div className="duvidas-text">
                  <h3>Dúvidas sobre sua avaliação?</h3>
                  <p>Nossa equipe técnica está pronta para dar o feedback detalhado por Mensagem.</p>
                </div>
                <button className="btn-white">TIRE SUA DÚVIDA</button>
              </div>

            </div>

          </div>
          )}

          {activeMenu === 'notas' && (
            <div className="notas-container">
              <div className="notas-header-section">
                <div className="notas-title-box">
                  <h3>MINHAS NOTAS</h3>
                  <p>Consulte seu boletim detalhado escolhendo uma das sessões finalizadas.</p>
                  
                  {/* NOVO: Caixa de Seleção de Sessão */}
                  <div className="session-selector-wrapper" style={{ marginTop: '15px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '6px' }}>SELECIONE A SESSÃO:</label>
                    <select 
                      value={selectedSessionId || ''} 
                      onChange={(e) => setSelectedSessionId(e.target.value)}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '2px solid #cbd5e1', fontSize: '14px', fontWeight: 600, color: '#1e293b', background: '#fff', width: '100%', maxWidth: '400px', cursor: 'pointer' }}
                    >
                      {finishedSessionsList.length === 0 ? (
                        <option value="">Nenhuma sessão avaliada ainda</option>
                      ) : (
                        finishedSessionsList.map(s => (
                          <option key={s.id} value={s.id}>{s.title}</option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
                
                <div className="notas-status-card">
                  <div className="status-icon"><TrendingUp size={24} /></div>
                  <div className="status-text">
                    <span>STATUS DA SESSÃO</span>
                    <strong>{competitionStateLabel}</strong>
                  </div>
                </div>
              </div>
              
              <div className="notas-kpi-row">
                <div className="media-geral-card">
                  <div className="media-geral-header">MÉDIA DA SESSÃO</div>
                  <div className="media-geral-value">
                    <strong>{hasRealNotes ? latestEffectiveScore.toFixed(2) : '--'}</strong>
                    <span>/ 10</span>
                  </div>
                  <div className="media-geral-stars" style={{ display: 'flex', gap: '4px', margin: '10px 0' }}>
                    {[1, 2, 3, 4, 5].map((starIndex) => {
                      const starLimit = hasRealNotes ? Math.round((latestEffectiveScore / 10) * 5) : 0;
                      const isFilled = starIndex <= starLimit;
                      return (
                        <Star
                          key={starIndex}
                          size={18}
                          fill={isFilled ? '#FFDE00' : 'transparent'}
                          color={isFilled ? '#FFDE00' : '#B7A96A'}
                        />
                      );
                    })}
                  </div>
                  <div className="media-geral-desc">
                    {hasRealNotes
                      ? `Sessão visualizada: ${currentActiveResult?.session?.title || ''}`
                      : 'Aguarde ser avaliado para ver suas notas.'}
                  </div>
                  <div className="media-geral-decor">♩</div>
                </div>
                
                <div className="criterios-card">
                  <h4>Pontuação Consolidada</h4>
                  <div className="criterio-bar-item">
                    <div className="criterio-bar-label">
                      <span>Média dos Jurados</span>
                      <strong>{hasRealNotes ? latestTechnicalScore.toFixed(2) : '--'}</strong>
                    </div>
                    <div className="criterio-bar-bg"><div className="criterio-bar-fill green" style={{ width: `${Math.min(100, Math.max(0, latestTechnicalScore * 10))}%` }}></div></div>
                  </div>
                    <div className="criterio-bar-item">
                    <div className="criterio-bar-label">
                      <span>Votos Públicos</span>
                      <strong>{hasRealNotes ? latestAdjustmentScore.toLocaleString('pt-BR') : '--'}</strong>
                    </div>
                    <div className="criterio-bar-bg"><div className="criterio-bar-fill olive" style={{ width: publicBarWidth }}></div></div>
                  </div>
                  <div className="criterio-bar-item">
                    <div className="criterio-bar-label">
                      <span>Resultado Efetivo</span>
                      <strong>{hasRealNotes ? latestEffectiveScore.toFixed(2) : '--'}</strong>
                    </div>
                    <div className="criterio-bar-bg"><div className="criterio-bar-fill blue" style={{ width: `${Math.min(100, Math.max(0, latestEffectiveScore * 10))}%` }}></div></div>
                  </div>
                </div>
              </div>

              <div className="jurados-table-section" style={{ marginTop: '30px' }}>
                <h4>Notas por Critério Avaliado ({currentActiveResult?.session?.title || ''})</h4>
                {!hasRealNotes ? (
                  <div style={{ padding: '12px 0' }}>Escolha uma sessão finalizada para ver o detalhamento das notas.</div>
                ) : (
                  (() => {
                    const judgeEntries = extractJudgeVotes(currentActiveResult) || [];
                    const judgesWithVotes = judgeEntries.filter((j) => {
                      if (!j || !j.scores) return false;
                      return Object.values(j.scores).some((v) => v !== null && v !== undefined && v !== '');
                    });

                    const judgesCount = judgeEntries.length;
                    const votesCount = judgesWithVotes.length;

                    return (
                      <>
                        <div className="jurados-table-wrap">
                          <table className="jurados-table judge-candidate-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9' }}>
                                <th style={{ padding: '12px', textAlign: 'left', minWidth: '150px' }}>Critério Técnico</th>
                                {judgeEntries.map((j) => (
                                  <th key={j.id || j.name} style={{ padding: '12px', textAlign: 'center' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                      {j.avatar ? (
                                        <img src={j.avatar} alt={j.name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                                      ) : (
                                        <div className="jurado-initials" style={{ width: 32, height: 32, borderRadius: '50%', background: '#cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold' }}>
                                          {(j.name || 'J').slice(0, 2).toUpperCase()}
                                        </div>
                                      )}
                                      <span style={{ fontSize: '12px', fontWeight: 700 }}>{j.name ? (j.name.length > 12 ? `${j.name.slice(0, 12)}...` : j.name) : 'Jurado'}</span>
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {SCORE_CRITERIA.map((crit) => (
                                <tr key={crit.key} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                  <td style={{ padding: '12px', fontWeight: 600, color: '#334155' }}>{crit.label}</td>
                                  {judgeEntries.map((j) => {
                                    const score = readCriterionFromResult(j.scores, crit.key);
                                    return (
                                      <td key={`${crit.key}-${j.id || j.name}`} style={{ padding: '12px', textAlign: 'center', fontWeight: 700, fontSize: '15px', color: score !== null ? '#0f172a' : '#94a3b8' }}>
                                        {score !== null ? score.toFixed(1) : '--'}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  })()
                )}
              </div>

              <div className="notas-actions" style={{ marginTop: '20px' }}>
                <p>
                  {hasRealNotes
                    ? `Melhor resultado alcançado: ${Number(bestResult?.effective_score || 0).toFixed(2)} pontos.`
                    : 'Assim que os jurados concluírem sua avaliação, os resultados aparecerão aqui automaticamente.'}
                </p>
                <div className="notas-buttons">
                  {/* <button className="btn-download-notas" type="button"><Download size={18} /> BAIXAR EXTRATO</button> */}
                  <button className="btn-recorrer" type="button" onClick={() => window.location.reload()}>ATUALIZAR DADOS</button>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'status' && (
            <div className="status-comp-container">
              <div className="status-comp-header">
                <div className="status-comp-title">
                  <h2>Sua Jornada no Arraial</h2>
                  <p>Acompanhe seu progresso rumo ao título de melhor sanfoneiro do Nordeste.</p>
                </div>
                <div className="status-comp-badge-card">
                  <div className={`badge-icon ${profile?.status === 'eliminado' ? 'red' : 'green'}`}>
                    {profile?.status === 'eliminado' ? <X size={28} strokeWidth={2.5} /> : <CheckCircle size={28} strokeWidth={2.5} />}
                  </div>
                  <div className="badge-info">
                    <span>STATUS ATUAL</span>
                    <strong>{competitionStateLabel}</strong>
                  </div>
                </div>
              </div>

              <div className="status-comp-body">
                {/* Timeline Column */}
                <div className="status-timeline-card">
                  <div className="timeline-header">
                    <Activity size={24} color="#7B692A" />
                    <h3>Fases da Competição</h3>
                  </div>
                  
                  <div className="timeline-container">
                    <div className="timeline-item">
                      <div className={`timeline-marker ${hasRealNotes ? 'concluded' : 'next'}`}>
                        {hasRealNotes ? <Check size={16} /> : <Clock size={16} />}
                      </div>
                      <div className={`timeline-content ${hasRealNotes ? 'concluded-card' : 'next-card'}`}>
                        <span className={`step-status ${hasRealNotes ? '' : 'next-status'}`}>
                          {hasRealNotes ? 'CONCLUÍDO' : 'AGUARDANDO'}
                        </span>
                        <h4>{hasRealNotes ? (currentActiveResult?.session?.title || 'Sessão finalizada mais recente') : 'Nenhuma sessão finalizada'}</h4>
                        <p>
                          {hasRealNotes
                            ? `Pontuação ${latestEffectiveScore.toFixed(2)} • posição ${latestPosition || '-'} de ${latestTotalCandidates || '-'}.`
                            : sessions.length === 0
                            ? 'Aguardando os dados...'
                            : 'Nenhuma sessão finalizada com resultado liberado até o momento.'}
                        </p>
                        <div className={`step-date ${hasRealNotes ? '' : 'next-date'}`}><Calendar size={12} /> {currentActiveResult?.session?.session_date ? formatSessionDate(currentActiveResult.session.session_date) : 'Data não definida'}</div>
                      </div>
                    </div>
                    
                    <div className="timeline-line"></div>
                    
                    <div className="timeline-item">
                      <div className="timeline-marker next">
                        <Star size={16} />
                      </div>
                      <div className="timeline-content next-card">
                        <span className="step-status next-status">ETAPA ANTERIOR</span>
                        <h4>{nextSession?.title || 'Aguardando nova etapa'}</h4>
                        <p>
                          {nextSession
                            ? `Prepare-se para a sessão ${nextSession.title}.`
                            : 'Ainda não há nova sessão agendada para o seu perfil.'}
                        </p>
                        <div className="step-date next-date"><Clock size={12} /> {nextSession?.session_date ? formatSessionDate(nextSession.session_date) : 'Em breve'}</div>
                      </div>
                    </div>
                    <div className="timeline-line-bottom"></div>
                  </div>
                </div>

                {/* Right Area Column */}
                <div className="status-right-column">
                  <div className="prep-hero-standalone" style={{ backgroundImage: "url('/img/theater_curtain.png')" }}>
                    <div className="prep-hero-overlay"></div>
                    <h3>O que Preparar para a apresentação</h3>
                  </div>
                  
                  <div className="prep-items-grid">
                    <div className="prep-item-card">
                      <div className="prep-item-card-icon olive">
                        <Music size={28} />
                      </div>
                      <div className="prep-item-card-body">
                        <h4>Música cadastrada</h4>
                        <p>{profile?.song_name || 'Você ainda não cadastrou a música da apresentação.'}</p>
                      </div>
                    </div>

                    <div className="prep-item-card">
                      <div className="prep-item-card-icon green">
                        <Shirt size={28} />
                      </div>
                      <div className="prep-item-card-body">
                        <h4>Figurino</h4>
                        <p>Escolha um figurino que combine com o seu estilo artístico e represente a cultura nordestina.</p>
                      </div>
                    </div>

                    <div className="prep-item-card">
                      <div className="prep-item-card-icon olive">
                        <Mic2 size={28} />
                      </div>
                      <div className="prep-item-card-body">
                        <h4>Afinação de voz</h4>
                        <p>Trabalhe a afinação vocal para garantir uma apresentação impecável no palco.</p>
                      </div>
                    </div>

                    <div className="prep-item-card">
                      <div className="prep-item-card-icon green">
                        <Users size={28} />
                      </div>
                      <div className="prep-item-card-body">
                        <h4>Coreografia</h4>
                        <p>Desenvolva movimentos e coreografia para complementar sua performance musical.</p>
                      </div>
                    </div>

                    <div className="prep-item-card">
                      <div className="prep-item-card-icon olive">
                        <FileText size={28} />
                      </div>
                      <div className="prep-item-card-body">
                        <h4>Músicas autorais</h4>
                        <p>Se tiver composições próprias, considere incluir no seu repertório de apresentação.</p>
                      </div>
                    </div>

                    <div className="prep-item-card">
                      <div className="prep-item-card-icon green">
                        <Star size={28} />
                      </div>
                      <div className="prep-item-card-body">
                        <h4>Interpretações diferentes</h4>
                        <p>Pratique interpretações variadas da música para surpreender e emocionar o público.</p>
                      </div>
                    </div>
                  </div>

                  <div className="prep-session-info">
                    <div className="prep-info-header">
                      <Calendar size={20} />
                      <span>Data e Horário da Apresentação</span>
                    </div>
                    <p className="prep-info-datetime">
                      {nextSession
                        ? `${formatSessionDate(nextSession.session_date)} às ${formatSessionTime(nextSession.session_time)}`
                        : 'Aguardando agendamento da próxima apresentação'}
                    </p>
                    <p className="prep-info-status">
                      {profile?.status === 'eliminado' ? '⚠️ Seu perfil está bloqueado para edição.' : '✓ Seu perfil está ativo para as próximas etapas.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom Cards Row */}
              <div className="status-bottom-cards">
                <div className="info-card blue-card">
                  <Map size={24} />
                  <div className="card-lbl">LOCAL</div>
                  <div className="card-val">{nextSession?.location || latestResult?.session?.location || 'A definir'}</div>
                </div>
                
                <div className="info-card yellow-card">
                  <Users size={24} />
                  <div className="card-lbl">SESSÕES</div>
                  <div className="card-val">{finishedSessionsCount} finalizadas<br />{upcomingSessionsCount} pendentes</div>
                </div>

                <div className="info-card beige-card">
                  <Gift size={24} />
                  <div className="card-lbl">POSIÇÃO ATUAL</div>
                  <div className="card-val">{hasRealNotes ? `${latestPosition || '-'}º de ${latestTotalCandidates || '-'}` : 'Sem ranking'}</div>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'apresentacao' && (
            <div className="apresentacao-container">
              <div className="apresentacao-header">
                <h2>Minha Apresentação</h2>
                <p>Acompanhe informações sobre sua apresentação no festival.</p>
              </div>

              <div className="apresentacao-content">
                {hasScheduleInfo ? (
                  <div className="apresentacao-scheduled-state">
                    <div className="scheduled-icon-row">
                      <Calendar size={64} />
                    </div>
                    <h3>Apresentação Agendada!</h3>
                    <div className="scheduled-details">
                      <div className="detail-item">
                        <span>Data:</span>
                        <strong>{formatSessionDate(nextSession?.session_date)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Horário:</span>
                        <strong>{formatSessionTime(nextSession?.session_time)}</strong>
                      </div>
                      <div className="detail-item">
                        <span>Local:</span>
                        <strong>{nextSession?.location || 'Local não definido'}</strong>
                      </div>
                    </div>
                    <p className="scheduled-state-info">Sua apresentação acontecerá na sessão "{nextSession?.title}". Chegue com pelo menos 1 hora de antecedência e procure a produção.</p>
                  </div>
                ) : (
                  <div className="apresentacao-empty-state">
                    <div className="empty-state-icon">
                      <Calendar size={64} />
                    </div>
                    <h3>Aguardando agendamento</h3>
                    <p>Ainda não há uma data agendada para sua apresentação.</p>
                    <p className="empty-state-info">Assim que a produção confirmar o horário e local, você receberá uma notificação e as informações aparecerão aqui.</p>
                  </div>
                )}

                <div className="apresentacao-checklist">
                  <h4>Prepare-se enquanto isso:</h4>
                  <div className="checklist-items">
                    <div className="checklist-item">
                      <div className="checklist-checkbox">
                        <Check size={20} />
                      </div>
                      <div className="checklist-text">
                        <span>Música selecionada</span>
                        <p>{profile?.song_name || 'Não informada'}</p>
                      </div>
                    </div>

                    <div className="checklist-item">
                      <div className="checklist-checkbox">
                        <Shirt size={20} />
                      </div>
                      <div className="checklist-text">
                        <span>Escolher figurino apropriado</span>
                        <p>Que combine com a cultura nordestina</p>
                      </div>
                    </div>

                    <div className="checklist-item">
                      <div className="checklist-checkbox">
                        <Mic2 size={20} />
                      </div>
                      <div className="checklist-text">
                        <span>Treinar afinação vocal</span>
                        <p>Garantir melhor desempenho no palco</p>
                      </div>
                    </div>

                    <div className="checklist-item">
                      <div className="checklist-checkbox">
                        <Users size={20} />
                      </div>
                      <div className="checklist-text">
                        <span>Desenvolver coreografia</span>
                        <p>Movimentos que complementem a performance</p>
                      </div>
                    </div>

                    <div className="checklist-item">
                      <div className="checklist-checkbox">
                        <Music size={20} />
                      </div>
                      <div className="checklist-text">
                        <span>Variar interpretações</span>
                        <p>Pratique diferentes formas de interpretar a música</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="apresentacao-info-card">
                  <div className="info-card-icon">
                    <Bell size={24} />
                  </div>
                  <div className="info-card-content">
                    <h5>Fique atento às notificações</h5>
                    <p>Você receberá uma notificação assim que sua data de apresentação for confirmada. Não esqueça de verificar seu perfil regularmente.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isEditModalOpen && (
            <div className="candidate-modal-overlay" onClick={closeEditModal}>
              <div className="candidate-modal" onClick={(event) => event.stopPropagation()}>
                <div className="candidate-modal-header">
                  <h3>Editar Perfil</h3>
                  <button type="button" className="candidate-modal-close" onClick={closeEditModal}>
                    <X size={18} />
                  </button>
                </div>

                <div className="candidate-photo-preview-row">
                  <img
                    src={modalPhotoPreview}
                    alt="Foto de perfil"
                    className="candidate-photo-preview"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = DEFAULT_USER_IMAGE;
                    }}
                  />
                  <div>
                    <strong>Foto de perfil</strong>
                    <p>Selecione uma imagem JPG, PNG ou WEBP (até 10MB) para atualizar sua foto.</p>
                    <label className="candidate-photo-upload-button" htmlFor="candidate-photo-input">
                      {selectedPhotoFile ? 'Trocar imagem' : 'Selecionar imagem'}
                    </label>
                    <input
                      id="candidate-photo-input"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handlePhotoChange}
                      disabled={!canEditProfile || isSavingProfile}
                      className="candidate-photo-upload-input"
                    />
                  </div>
                </div>

                <form className="candidate-modal-form" onSubmit={handleSaveProfile}>
                  <div className="candidate-modal-grid">
                    <div className="candidate-modal-field">
                      <label htmlFor="name">Nome completo</label>
                      <input id="name" name="name" value={editForm.name} onChange={handleEditInputChange} disabled={!canEditProfile || isSavingProfile} required />
                    </div>

                    <div className="candidate-modal-field">
                      <label htmlFor="artistic_name">Nome artístico</label>
                      <input id="artistic_name" name="artistic_name" value={editForm.artistic_name} onChange={handleEditInputChange} disabled={!canEditProfile || isSavingProfile} required />
                    </div>

                    <div className="candidate-modal-field">
                      <label htmlFor="birth_date">Data de nascimento</label>
                      <input id="birth_date" type="date" name="birth_date" value={editForm.birth_date} onChange={handleEditInputChange} disabled={!canEditProfile || isSavingProfile} />
                    </div>

                    <div className="candidate-modal-field">
                      <label htmlFor="phone">Telefone</label>
                      <input id="phone" name="phone" value={editForm.phone} onChange={handlePhoneChange} disabled={!canEditProfile || isSavingProfile} placeholder="(xx) x xxxx-xxxx" />
                    </div>

                    <div className="candidate-modal-field candidate-modal-field-full">
                      <label htmlFor="address">Endereço</label>
                      <input id="address" name="address" value={editForm.address} onChange={handleEditInputChange} disabled={!canEditProfile || isSavingProfile} />
                    </div>

                    <div className="candidate-modal-field candidate-modal-field-full">
                      <label htmlFor="song_name">Música da apresentação</label>
                      <input
                        id="song_name"
                        name="song_name"
                        value={editForm.song_name}
                        onChange={handleEditInputChange}
                        disabled={!canEditProfile || isSavingProfile || !canEditSongName}
                        placeholder={canEditSongName ? 'Informe sua música' : 'Música já definida'}
                      />
                      {!canEditSongName && (
                        <small className="candidate-modal-help">Música já definida. A troca só pode ser feita na próxima fase.</small>
                      )}
                    </div>
                  </div>

                  {editError && <div className="candidate-modal-error">{editError}</div>}

                  <div className="candidate-modal-actions">
                    <button type="button" className="candidate-modal-cancel" onClick={closeEditModal} disabled={isSavingProfile}>
                      Cancelar
                    </button>
                    <button type="submit" className="candidate-modal-save" disabled={isSavingProfile || !canEditProfile}>
                      {isSavingProfile ? 'Salvando...' : 'Salvar alterações'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {isIncompleteProfileModalOpen && (
            <div className="candidate-modal-overlay" onClick={() => setIsIncompleteProfileModalOpen(false)}>
              <div className="candidate-modal incomplete-profile-modal" onClick={(event) => event.stopPropagation()}>
                <div className="candidate-modal-header">
                  <h3>⚠️ Itens Faltando no Perfil</h3>
                  <button type="button" className="candidate-modal-close" onClick={() => setIsIncompleteProfileModalOpen(false)}>
                    <X size={18} />
                  </button>
                </div>

                <div className="incomplete-profile-content">
                  <p className="incomplete-profile-message">
                    Seu perfil ainda está incompleto. Complete os itens abaixo para melhorar sua experiência:
                  </p>
                  
                  <div className="incomplete-profile-items-list">
                    {incompleteProfileItems.map((item, index) => (
                      <div key={index} className="incomplete-item">
                        <div className="incomplete-item-icon">❌</div>
                        <div className="incomplete-item-text">
                          <strong>{item}</strong>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="incomplete-profile-actions">
                    <button 
                      type="button" 
                      className="btn-incomplete-close" 
                      onClick={() => setIsIncompleteProfileModalOpen(false)}
                    >
                      Fechar
                    </button>
                    <button 
                      type="button" 
                      className="btn-incomplete-edit" 
                      onClick={() => {
                        setIsIncompleteProfileModalOpen(false);
                        openEditModal();
                      }}
                    >
                      Atualizar Perfil
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <footer className="site-footer">
            <div className="footer-diamonds">
              <div className="diamond green"></div>
              <div className="diamond yellow"></div>
              <div className="diamond blue"></div>
              <div className="diamond red"></div>
            </div>
            <h3>FESTIVAL DE FORRÓ 2026</h3>
            <p>© 2026 JR Produtora - O CORAÇÃO DO NORDESTE BATE AQUI.</p>
            <div className="footer-links">
              <button type="button">PRIVACIDADE</button>
              <button type="button">TERMOS DE USO</button>
              {/* <button type="button">CONTATO</button> */}
              <button type="button">EDITAL</button>
            </div>
          </footer>

        </main>
      </div>
    </div>
  );
};

export default CandidateArea;
